'use client';

import { Box, IconButton, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import { Search, StandardInfo } from '@castlery/fortress/Icons';
import { Product, Variant } from '@castlery/modules-product-domain';
import { FortressImage, PinchZoomViewer } from '@castlery/shared-components';
import { useCallback, useMemo, useState } from 'react';
import { PropertyExplanationDrawer } from './property-explanation-drawer';
import { EVENT_PDP_DETAILS } from '@castlery/modules-tracking-services';
import { useAppDispatch } from '@castlery/shared-redux-store';

interface ProductDimensionComfortProps {
  product?: Product;
  variant?: Variant;
}

export const ProductDimensionComfort = (props: ProductDimensionComfortProps) => {
  const { product, variant } = props;
  const { desktop } = useBreakpoints();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [openPinchZoom, setOpenPinchZoom] = useState(false);
  const [explanation, setExplanation] = useState('');
  //   const timerRef = useRef(null);
  const targetLinks = useMemo(() => {
    return variant?.dimension_image?.links || product?.dimension_image?.links;
  }, [product, variant]);

  const handleTrackPDPDetails = useCallback(async () => {
    await dispatch(
      EVENT_PDP_DETAILS({
        action: 'product_property',
        label: `Dimensions Image`,
      })
    );
  }, [dispatch]);

  const handleDimensionImageClick = useCallback(() => {
    // op 中会将 handleResetTime 传入，在 pinchzoom 中需要实现相关追踪
    setOpenPinchZoom(true);
    handleTrackPDPDetails();
  }, []);

  //   const handleResetTime = () => {
  //     if (timerRef.current) {
  //       clearTimeout(timerRef.current);
  //     }
  //   };

  //   useEffect(
  //     () => () => {
  //       handleResetTime();
  //     },
  //     []
  //   );

  return (
    <>
      <Stack>
        {targetLinks && (
          <Box
            position="relative"
            onClick={handleDimensionImageClick}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                '& .overlay': {
                  opacity: 1,
                },
              },
            }}
          >
            <FortressImage
              src={targetLinks?.feed}
              alt={`dimension of ${product?.name}`}
              ratio={1.5}
              sizes={['0.4-md', '1-sm', '1-xs']}
            />
            {desktop && (
              <Box
                className="overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.25)',
                  opacity: 0,
                  transition: 'opacity 0.3s',
                }}
              >
                {/* 遮罩层 icon*/}
                <Stack justifyContent={'center'} alignItems={'center'} spacing={2}>
                  <IconButton
                    variant="image"
                    sx={{
                      width: '40px',
                      height: '40px',
                      '& svg': {
                        color: 'var(--fortress-palette-brand-mono-900)',
                      },
                    }}
                    onClick={handleDimensionImageClick}
                  >
                    <Search />
                  </IconButton>
                  <Typography
                    level="body1"
                    sx={{
                      color: 'var(--fortress-palette-brand-warmLinen-200)',
                    }}
                  >
                    Click to expand
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        )}
        <Stack>
          {((variant?.variant_properties?.comfort_ratings?.length || 0) > 0 ||
            (product?.product_properties?.comfort_ratings?.length || 0) > 0) && (
            <Stack
              my={5}
              sx={{
                '& > *:not(:first-child)': {
                  borderTop: 'none',
                },
              }}
            >
              <ComfortRating
                name="overall_sit_rating"
                totalScore={5}
                minScorePresentation="Relaxed"
                maxScorePresentation="Upright"
                product={product}
                variant={variant}
                handleDrawerOpen={setOpen}
                handleInfoClick={setExplanation}
              />
              <ComfortRating
                name="seat_depth_rating"
                totalScore={5}
                minScorePresentation="Shallow"
                maxScorePresentation="Deep"
                product={product}
                variant={variant}
                handleDrawerOpen={setOpen}
                handleInfoClick={setExplanation}
              />
              <ComfortRating
                name="seat_height_rating"
                totalScore={5}
                minScorePresentation="Low"
                maxScorePresentation="High"
                product={product}
                variant={variant}
                handleDrawerOpen={setOpen}
                handleInfoClick={setExplanation}
              />
              <ComfortRating
                name="seat_softness_rating"
                totalScore={5}
                minScorePresentation="Soft"
                maxScorePresentation="Firm"
                product={product}
                variant={variant}
                handleDrawerOpen={setOpen}
                handleInfoClick={setExplanation}
              />
              <ComfortRating
                name="pillow_density_rating"
                totalScore={3}
                product={product}
                variant={variant}
                handleDrawerOpen={setOpen}
                handleInfoClick={setExplanation}
              />
              <ComfortRating
                name="pillow_support_rating"
                totalScore={3}
                product={product}
                variant={variant}
                handleDrawerOpen={setOpen}
                handleInfoClick={setExplanation}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
      <PropertyExplanationDrawer open={open} onClose={() => setOpen(false)} explanation={explanation} />
      <PinchZoomViewer
        open={openPinchZoom}
        setOpen={setOpenPinchZoom}
        slideImages={[
          {
            width: 100,
            height: 100,
            alt: 'dimension image of product',
            src: targetLinks?.large_gray || '',
            assetPosition: 'product dimension',
            assetType: 'dimension',
          },
        ]}
        index={0}
      />
    </>
  );
};

interface ComfortRatingProps {
  product?: Product;
  variant?: Variant;
  name: string;
  totalScore: number;
  minScorePresentation?: string;
  maxScorePresentation?: string;
  handleDrawerOpen?: (open: boolean) => void;
  handleInfoClick?: (explanation: string) => void;
}

export const ComfortRating = (props: ComfortRatingProps) => {
  const {
    product,
    variant,
    name,
    totalScore,
    minScorePresentation,
    maxScorePresentation,
    handleDrawerOpen,
    handleInfoClick,
  } = props;
  const { mobile } = useBreakpoints();
  const targetRating = useMemo(() => {
    return (
      variant?.variant_properties?.comfort_ratings?.find((it) => it.name === name) ||
      product?.product_properties?.comfort_ratings?.find((it) => it.name === name)
    );
  }, [name, product, variant]);

  if (!targetRating) {
    return null;
  }

  return (
    <>
      <Stack
        py={mobile ? 3 : 5}
        sx={{
          border: '0.5px solid var(--fortress-palette-brand-mono-200)',
        }}
      >
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
          <Typography
            level="subh3"
            endDecorator={
              targetRating?.explanation && (
                <IconButton
                  type="button"
                  onClick={() => {
                    handleInfoClick?.(targetRating?.explanation || '');
                    handleDrawerOpen?.(true);
                  }}
                  sx={{
                    width: '20px',
                    height: '20px',
                    svg: {
                      path: {
                        fill: 'var(--fortress-palette-brand-mono-900)',
                      },
                    },
                  }}
                >
                  {/* <ProductInfoTooltip /> */}
                  <StandardInfo sx={{ width: '20px', height: '20px' }} />
                </IconButton>
              )
            }
            sx={{ textTransform: 'uppercase' }}
          >
            {targetRating?.presentation}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="center" alignItems="center" mt={3}>
          {minScorePresentation && (
            <Typography
              level="caption1"
              sx={{
                flex: '1 1 0',
                textAlign: 'right',
              }}
            >
              {minScorePresentation}
            </Typography>
          )}
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            mx={4}
            spacing={2}
            sx={{
              flex: '0 0 auto',
            }}
          >
            {targetRating?.value && (
              <>
                {Array.from({ length: totalScore }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: mobile ? 24 : 32,
                      height: 12,
                      bgcolor:
                        Number(targetRating?.value) - 1 === index
                          ? 'var(--fortress-palette-primary-500)'
                          : 'var(--fortress-palette-primary-100)',
                    }}
                  />
                ))}
              </>
            )}
          </Stack>
          {maxScorePresentation && (
            <Typography
              level="caption1"
              sx={{
                flex: '1 1 0',
                textAlign: 'left',
              }}
            >
              {maxScorePresentation}
            </Typography>
          )}
        </Stack>
      </Stack>
    </>
  );
};
